import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getMyTickets } from '../services/api';
import Navbar from '../components/Navbar';
import { useTheme } from '../context/ThemeContext';
import useWindowSize from '../hooks/useWindowSize';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';

function MyTickets() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedQr, setSelectedQr] = useState(null);
  const navigate = useNavigate();
  const { colors } = useTheme();
  const { width } = useWindowSize();
  const isMobile = width < 768;

  useEffect(() => {
    const user = localStorage.getItem('user');
    if (!user) { navigate('/'); return; }
    getMyTickets()
      .then(res => { setTickets(res.data); setLoading(false); })
      .catch(err => { setError(err.response?.data?.error || 'Error al cargar las entradas'); setLoading(false); });
  }, [navigate]);

  return (
    <div style={{ minHeight: '100vh', backgroundColor: colors.bg }}>
      <Navbar />

      {selectedQr && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.85)', zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }} onClick={() => setSelectedQr(null)}>
          <div style={{ backgroundColor: colors.bgSecondary, borderRadius: '16px', padding: '2rem', textAlign: 'center', width: '100%', maxWidth: '300px' }} onClick={e => e.stopPropagation()}>
            <h3 style={{ color: colors.text, marginBottom: '1rem', fontSize: '1rem' }}>Código QR de tu entrada</h3>
            <img src={selectedQr} alt="QR" style={{ width: '200px', height: '200px' }} />
            <p style={{ color: colors.textSecondary, fontSize: '0.8rem', marginTop: '1rem', marginBottom: '1rem' }}>Muestra este QR en la entrada del evento</p>
            <button style={{ backgroundColor: colors.accent, color: colors.accentText, border: 'none', padding: '0.6rem 1.5rem', borderRadius: isMobile ? '4px' : '20px', cursor: 'pointer', fontWeight: 'bold', width: '100%' }} onClick={() => setSelectedQr(null)}>
              Cerrar
            </button>
          </div>
        </div>
      )}

      <div style={{ padding: isMobile ? '1rem' : '2rem', maxWidth: '800px', margin: '0 auto' }}>
        <h1 style={{ color: colors.text, fontSize: isMobile ? '1.4rem' : '2rem', fontWeight: 'bold', marginBottom: '1.5rem' }}>
          Mis entradas
        </h1>

        {loading ? (
          <p style={{ color: colors.textSecondary }}>Cargando entradas...</p>
        ) : error ? (
          <p style={{ color: '#ff4444' }}>{error}</p>
        ) : tickets.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '4rem 2rem' }}>
            <ConfirmationNumberIcon style={{ fontSize: '3rem', color: colors.border, marginBottom: '1rem' }} />
            <p style={{ color: colors.textSecondary, fontSize: '1rem', marginBottom: '1rem' }}>No tienes entradas todavía</p>
            <button style={{ backgroundColor: colors.accent, color: colors.accentText, border: 'none', padding: '0.7rem 2rem', borderRadius: isMobile ? '4px' : '20px', cursor: 'pointer', fontWeight: 'bold' }} onClick={() => navigate('/')}>
              Ver eventos
            </button>
          </div>
        ) : (
          <div>
            {tickets.map(ticket => (
              <div key={ticket.id} style={{
                padding: isMobile ? '1rem 0' : '1.5rem',
                marginBottom: isMobile ? '0' : '1rem',
                backgroundColor: isMobile ? 'transparent' : colors.bgSecondary,
                borderRadius: isMobile ? '0' : '16px',
                border: isMobile ? 'none' : `1px solid ${colors.border}`,
                borderBottom: `1px solid ${colors.border}`,
              }}>
                {ticket.event_image && (
                  <img src={ticket.event_image} alt={ticket.event_name} style={{ width: '100%', height: isMobile ? '160px' : '0px', objectFit: 'cover', borderRadius: '8px', marginBottom: '0.8rem', display: isMobile ? 'block' : 'none' }} />
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem' }}>
                  <div style={{ flex: 1 }}>
                    <p style={{ color: colors.accent, fontSize: '0.72rem', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.2rem' }}>
                      {ticket.used ? 'Utilizada' : 'Válida'}
                    </p>
                    <h3 style={{ color: colors.text, fontSize: isMobile ? '1rem' : '1.1rem', fontWeight: 'bold', marginBottom: '0.4rem' }}>{ticket.event_name}</h3>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.2rem' }}>
                      <CalendarTodayIcon style={{ color: colors.textSecondary, fontSize: 13 }} />
                      <span style={{ color: colors.textSecondary, fontSize: '0.8rem' }}>{new Date(ticket.event_date).toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.8rem' }}>
                      <LocationOnIcon style={{ color: colors.textSecondary, fontSize: 13 }} />
                      <span style={{ color: colors.textSecondary, fontSize: '0.8rem' }}>{ticket.event_location}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      {ticket.used ? (
                        <><CancelIcon style={{ color: '#ff4444', fontSize: 16 }} /><span style={{ color: '#ff4444', fontSize: '0.82rem', fontWeight: 'bold' }}>Entrada utilizada</span></>
                      ) : (
                        <><CheckCircleIcon style={{ color: '#00cc66', fontSize: 16 }} /><span style={{ color: '#00cc66', fontSize: '0.82rem', fontWeight: 'bold' }}>Entrada válida</span></>
                      )}
                    </div>
                  </div>
                  <div style={{ textAlign: 'center', flexShrink: 0 }}>
                    {!isMobile && ticket.event_image && (
                      <img src={ticket.event_image} alt={ticket.event_name} style={{ width: '80px', height: '80px', objectFit: 'cover', borderRadius: '8px', marginBottom: '0.5rem', display: 'block' }} />
                    )}
                    <button style={{ backgroundColor: ticket.used ? colors.bgTertiary : colors.accent, color: ticket.used ? colors.textSecondary : colors.accentText, border: 'none', padding: '0.5rem 1rem', borderRadius: isMobile ? '4px' : '20px', cursor: ticket.used ? 'default' : 'pointer', fontWeight: 'bold', fontSize: '0.82rem', textTransform: 'uppercase' }}
                      onClick={() => !ticket.used && setSelectedQr(ticket.qr_code)}
                      disabled={ticket.used}>
                      Ver QR
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default MyTickets;